import {
  Box,
  Button,
  Flex,
  FormLabel,
  Select,
  Text,
  useColorModeValue,
  useToast,
  Table,
  Thead,
  Tbody,
  Td,
  Tr,
  Th,
  Spinner,
} from "@chakra-ui/react";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import CardHeader from "components/Card/CardHeader";
import React, { useEffect, useState } from "react";
import { useRecoilValue } from "recoil";
import { useForm, Controller } from "react-hook-form";
import { useBookingActions } from "hooks/useBookingActions";
import { BsSearch } from "react-icons/bs";
import { RxReset } from "react-icons/rx";
import DatePickerInput from "components/CustomDateInput/CustomDateInput";
import { RiFileExcel2Fill } from "react-icons/ri";
import { listTheaterPassReportAtom } from "../../state/report";
import { useReportActions } from "hooks/useReportActions";
import { totalRow } from 'state/roles-permissions';

import { listShowAtom, venueListAtom, listTheaterPassAtom, showTimeListAtom } from "../../state/show";
import { getFirstAndLastDate } from "helpers/date";

function DataTableTheaterPassReport() {
  const listTheaterPassReport = useRecoilValue(listTheaterPassReportAtom);
  const reportActions = useReportActions();
  const textColor = useColorModeValue("gray.700", "white");
  const bookingActions = useBookingActions();
  const listShow = useRecoilValue(listShowAtom);
  const venueList = useRecoilValue(venueListAtom);
  const [isFetching, setIsFetching] = useState(true);
  const [isExportExcel, setIsExportExcel] = useState(false);
  const [highlightDate, setHighlightDate] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [isFilterDirty, setIsFilterDirty] = useState(false);
  const toast = useToast();
  
  const {
    register: registerSearch,
    handleSubmit: handleSubmitSearch,
    control: controlSearch,
    watch: watchSearch,
    reset: resetSearch,
    setValue: setValueSearch,
    formState,
  } = useForm();
  const { errors, isSubmitting } = formState;
  const showSearch = watchSearch("showSearch");
  const venueSearch = watchSearch("venueSearch");
  const allSearchValues = watchSearch();

  useEffect(() => {
    if (hasSearched) {
      setIsFilterDirty(true);
    }
  }, [JSON.stringify(allSearchValues)]);

  useEffect(async () => {
    setIsFetching(true);

    bookingActions.getListShow({ status: 1 }, 0, "");
    bookingActions.getListVenue({ status: 1 }, 0, "");
    bookingActions.getListShowTime({ status: 1 }, 0, '');
    setValueSearch("from_showdate", new Date());
    setValueSearch("to_showdate", new Date());
    
    // Only load data when user clicks Search button
    // reportActions.getListTheaterPassReport({
    //   show_date_from: new Date(new Date().setHours(0, 0, 0, 0)).toISOString(),
    //   show_date_to: new Date(
    //     new Date().setHours(23, 59, 59, 999)
    //   ).toISOString(),
    // });
    
    setIsFetching(false);
  }, []);

  async function onSubmitSearch({ showSearch, venueSearch, }) {
    if (!watchSearch("from_showdate") || !watchSearch("to_showdate")) {
      toast({
        description: 'Please select both Show Date From and Show Date To',
        status: 'warning',
        duration: 4000,
        isClosable: true,
        position: 'top-right',
      });
      if (isExportExcel) setIsExportExcel(false);
      return;
    }

    const paramSearch = {
      show_id: showSearch,
      venue_id: venueSearch,
      show_date_from: new Date(
        watchSearch("from_showdate").setHours(0, 0, 0, 0)
      ).toISOString(),
      show_date_to: new Date(
        watchSearch("to_showdate").setHours(23, 59, 59, 999)
      ).toISOString(),
    }

    if (isExportExcel) {
      setIsExportExcel(false);
      const response = await reportActions.getListTheaterPassExport(paramSearch);
      const outputFilename = `theater_pass_report_${Date.now()}.xlsx`;
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', outputFilename);
      document.body.appendChild(link);
      link.click();
      return;
    }
    setIsFetching(true);
    await reportActions.getListTheaterPassReport(paramSearch);
    setHasSearched(true);
    setIsFilterDirty(false);
    setIsFetching(false);
  }

  const handleCalendarOpen = async (year, month, isFrom = false) => {
    const dateToCheck = isFrom ? watchSearch("from_showdate") : watchSearch("to_showdate");
    const fallbackDate = new Date();
    
    const targetYear = year || (dateToCheck ? dateToCheck.getFullYear() : fallbackDate.getFullYear());
    const targetMonth = month ?? (dateToCheck ? dateToCheck.getMonth() : fallbackDate.getMonth());

    const { firstDate, lastDate } = getFirstAndLastDate(
      targetYear,
      targetMonth
    );

    try {
      const params = {
        from_date: firstDate,
        to_date: lastDate,
      };
      
      // Only add show_id if it has a value
      if (showSearch) {
        params.show_id = showSearch;
      }
      
      // Only add venue_id if it has a value
      if (venueSearch) {
        params.venue_id = venueSearch;
      }

      const res = await bookingActions.getShowsSchedule(params);

      if (res) {
        const dateHighlight = res.map((item) => new Date(item.showdate));
        setHighlightDate(dateHighlight);
      }
    } catch (error) {
      setError("apiError", { message: "Failed to fetch show schedule" });
    }
  };

  return (
    <Card px="0px">
      <CardHeader px="22px" mb="12px">
        <Flex direction="column" display="block">
          <Text
            float="left"
            color={textColor}
            fontSize="lg"
            fontWeight="bold"
            mb="6px"
          >
            Theater Pass Report
          </Text>
        </Flex>
      </CardHeader>
      <CardBody>
        <Flex ml="22px"></Flex>
        <Card>
          <form
            id="booking-search-form"
            onSubmit={handleSubmitSearch(onSubmitSearch)}
          >
            <Flex
              direction="row"
              alignItems="center"
              mb={3}
              gap={4}
              flexWrap={"wrap"}
            >
              <Box direction="row" alignItems="center" display="block">
                <FormLabel fontWeight="semibold" fontSize="xs" ms="4px">
                  Show
                </FormLabel>
                <Select
                  ms="4px"
                  type="text"
                  defaultValue="0"
                  size="sm"
                  id="showSearch"
                  name="showSearch"
                  {...registerSearch("showSearch")}
                  width="100px"
                >
                  <option value="">All</option>
                  {listShow.map((item, index) => (
                    <option key={index} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box direction="row" alignItems="center" display="block">
                <FormLabel fontWeight="semibold" fontSize="xs" ms="4px">
                  Venue
                </FormLabel>
                <Select
                  ms="4px"
                  type="text"
                  size="sm"
                  id={"venueSearch"}
                  name="venueSearch"
                  {...registerSearch("venueSearch")}
                >
                  <option value="">All</option>
                  {venueList.map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.name}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box direction="row" alignItems="center" display="block">
                <FormLabel ms="4px" fontSize="sm" fontWeight="bold">
                  Show Date From
                </FormLabel>
                <Controller
                  name="from_showdate"
                  control={controlSearch}
                  render={({ field }) => (
                    <DatePickerInput
                      onChange={(value) => {
                        if (value) {
                          handleCalendarOpen(
                            value.getFullYear(),
                            value.getMonth(),
                            true
                          );
                        }
                        field.onChange(value);
                      }}
                      onCalendarOpen={() => {
                        const fromDate = watchSearch("from_showdate");
                        if (fromDate) {
                          handleCalendarOpen(
                            fromDate.getFullYear(),
                            fromDate.getMonth(),
                            true
                          );
                        }
                      }}
                      mask="99/99/9999"
                      selected={watchSearch("from_showdate")}
                      highlightDates={highlightDate}
                      selectsStart
                      startDate={watchSearch("from_showdate")}
                      endDate={watchSearch("to_showdate")}
                      isShowMinDate
                    />
                  )}
                />
              </Box>
              <Box direction="row" alignItems="center" display="block">
                <FormLabel ms="4px" fontSize="sm" fontWeight="bold">
                  Show Date To
                </FormLabel>
                <Controller
                  name="to_showdate"
                  control={controlSearch}
                  render={({ field }) => (
                    <DatePickerInput
                      onChange={(value) => {
                        if (value) {
                          handleCalendarOpen(
                            value.getFullYear(),
                            value.getMonth(),
                            false
                          );
                        }
                        field.onChange(value);
                      }}
                      onCalendarOpen={() => {
                        const toDate = watchSearch("to_showdate");
                        if (toDate) {
                          handleCalendarOpen(
                            toDate.getFullYear(),
                            toDate.getMonth(),
                            false
                          );
                        }
                      }}
                      highlightDates={highlightDate}
                      selected={field.value}
                      mask="99/99/9999"
                      selectsEnd
                      startDate={watchSearch("from_showdate")}
                      endDate={watchSearch("to_showdate")}
                      isShowMinDate
                    />
                  )}
                />
              </Box>
            </Flex>
            <Text color="red" fontSize={14}>
              {Object.values(errors)[0]?.message}
            </Text>
            
            <Flex justify="center" align="center" gap="8px" mt={3}>
              <Box direction="row" alignItems="center" display="block">
                <Button
                  leftIcon={<RxReset />}
                  variant="primary"
                  w="88px"
                  h="33px"
                  onClick={() => {
                    resetSearch();
                    setValueSearch("from_showdate", new Date());
                    setValueSearch("to_showdate", new Date());
                  }}
                >
                  Reset
                </Button>
              </Box>
              <Box direction="row" alignItems="center" display="block">
                <Button
                  leftIcon={<RiFileExcel2Fill />}
                  variant="primary"
                  h="33px"
                  type="submit"
                  isDisabled={!hasSearched || isFilterDirty || listTheaterPassReport.length === 0}
                  onClick={() => {
                    setIsExportExcel(true);
                  }}
                >
                  Export Excel
                </Button>
              </Box>
              <Box direction="row" alignItems="center" display="block">
                <Button
                  leftIcon={<BsSearch />}
                  variant="primary"
                  w="88px"
                  h="33px"
                  type="submit"
                >
                  Search
                </Button>
              </Box>
            </Flex>
          </form>
        </Card>

        {isFetching ? (
          <Flex justify="center" align="center" my="20px">
            <Spinner
              textAlign="center"
              thickness="4px"
              speed="0.65s"
              emptyColor="gray.200"
              color="blue.500"
              size="xl"
            />
          </Flex>
        ) : listTheaterPassReport.length === 0 ? (
          <Box overflowX={"auto"}>
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th color="white" colSpan="5" backgroundColor="blue.600">
                    No data
                  </Th>
                </Tr>
              </Thead>
              <Thead>
                <Tr>
                  <Th w={"450px"}></Th>
                  <Th textAlign="center">aah![A]</Th>
                  <Th textAlign="center">ooh![O]</Th>
                  <Th textAlign="center">wow![W]</Th>
                  <Th textAlign="center">Total</Th>
                </Tr>
              </Thead>
            </Table>
          </Box>
        ) : (
          listTheaterPassReport.map((report, reportIndex) => (
            <Box overflowX={"auto"}>
              <Table size="sm" key={reportIndex}>
                <Thead>
                  <Tr>
                    <Th color="white" colSpan="5" backgroundColor="blue.600">
                      {`${report.show_date} ${report.show_time}`}
                    </Th>
                  </Tr>
                  <Tr>
                    <Th w={"450px"}></Th>
                    <Th textAlign="center">aah![A]</Th>
                    <Th textAlign="center">ooh![O]</Th>
                    <Th textAlign="center">wow![W]</Th>
                    <Th textAlign="center">Total</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {report.data.map((row, index) => (
                    <Tr key={index}>
                      <Td>{row.title}</Td>
                      <Td textAlign="center">{row.aah}</Td>
                      <Td textAlign="center">{row.ooh}</Td>
                      <Td textAlign="center">{row.wow}</Td>
                      <Td textAlign="center">{row.total}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          ))
        )}
      </CardBody>
    </Card>
  );
}

export default DataTableTheaterPassReport;
